
import { Component, Vue } from 'vue-property-decorator';
import GeneEnum from '../enums/gene.enum';
import ApplicationOptions from '../interfaces/application-options';
import { isScanningAvailable } from '@/lib/ui-utils';

/**
 * Version stored options in localStorage; increment if the shape changes so stale data is ignored.
 */
const OPTIONS_VERSION = 5;
const hardwareConcurrency = navigator.hardwareConcurrency || 4;
/** Default workers ≈ 65% of logical cores: strong throughput without maxing every core (cooler / less battery drain). */
function defaultWorkerCountForPowerEfficiency(): number {
  return Math.max(1, Math.round(hardwareConcurrency * 0.65));
}
const OPTIONS_STORAGE_KEY = `rosi-breeder-options-v${OPTIONS_VERSION}`;
const LEGACY_OPTIONS_STORAGE_KEY = 'rosi-breeder-options-v4';

const DEFAULT_OPTIONS: ApplicationOptions = {
  withRepetitions: true,
  modifyMinimumTrackedScoreManually: false,
  minCrossbreedingSaplingsNumber: 2,
  maxCrossbreedingSaplingsNumber: 5,
  numberOfGenerations: 2,
  numberOfSaplingsAddedBetweenGenerations: 20,
  minimumTrackedScore: 4,
  geneScores: {
    [GeneEnum.G]: 1,
    [GeneEnum.Y]: 1,
    [GeneEnum.H]: 0.5,
    [GeneEnum.X]: 0,
    [GeneEnum.W]: 0
  },
  darkMode: true,
  skipScannerGuide: false,
  scannerOffsetX: 0,
  scannerOffsetY: 0,
  scannerGeneScale: 1,
  autoSaveInputSets: true,
  sounds: true,
  numberOfWorkers: defaultWorkerCountForPowerEfficiency()
};

function mergeStoredOptions(parsed: Partial<ApplicationOptions>): ApplicationOptions {
  const base = JSON.parse(JSON.stringify(DEFAULT_OPTIONS)) as ApplicationOptions;
  return {
    ...base,
    ...parsed,
    geneScores: { ...base.geneScores, ...(parsed.geneScores || {}) },
    scannerOffsetX: typeof parsed.scannerOffsetX === 'number' ? parsed.scannerOffsetX : base.scannerOffsetX,
    scannerOffsetY: typeof parsed.scannerOffsetY === 'number' ? parsed.scannerOffsetY : base.scannerOffsetY,
    scannerGeneScale: typeof parsed.scannerGeneScale === 'number' ? parsed.scannerGeneScale : base.scannerGeneScale
  };
}

function loadOptionsFromStorage(): ApplicationOptions {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(OPTIONS_STORAGE_KEY) || localStorage.getItem(LEGACY_OPTIONS_STORAGE_KEY);
  } catch {
    raw = null;
  }
  if (!raw) {
    return JSON.parse(JSON.stringify(DEFAULT_OPTIONS));
  }
  try {
    return mergeStoredOptions(JSON.parse(raw));
  } catch {
    return JSON.parse(JSON.stringify(DEFAULT_OPTIONS));
  }
}

@Component
export default class Options extends Vue {
  isDialogOpen = false;
  isFormValid = false;
  tab = null;

  currentlySetOptions: ApplicationOptions = loadOptionsFromStorage();
  options: ApplicationOptions = JSON.parse(JSON.stringify(this.currentlySetOptions));

  crossbreedingSaplingsNumberRange = [
    this.options.minCrossbreedingSaplingsNumber,
    this.options.maxCrossbreedingSaplingsNumber
  ];

  maxCrossbreedingSaplingsLabels = ['2', '3', '4', '5', '6', '7', '8'];
  shouldDisplayScreenCaptureOptions = false;

  get numberOfGenerationLabels(): string[] {
    return [
      String(this.$t('options.genTickOne')),
      String(this.$t('options.genTickTwo')),
      String(this.$t('options.genTickThree'))
    ];
  }

  get geneScoreRules() {
    const msg = String(this.$t('options.geneScoreRule'));
    return [(v: number | string) => (v !== '' && Number(v) >= -1 && Number(v) <= 1) || msg];
  }

  get numberOfSaplingsAddedBetweenGenerationsRules() {
    const msg = String(this.$t('options.saplingsAddedRule'));
    return [(v: number | string) => (v !== '' && Number(v) >= 1 && Number.isInteger(Number(v))) || msg];
  }

  get minimumTrackedScoreRules() {
    const msg = String(this.$t('options.minTrackedRule'));
    return [(v: number | string) => (v !== '' && Number(v) >= -6 && Number(v) <= 6) || msg];
  }

  get maxNumberOfWorkers() {
    const cores = hardwareConcurrency || 4;
    return Math.min(32, Math.max(cores, cores * 2));
  }

  get numberOfWorkersLabels() {
    const modBy = this.maxNumberOfWorkers >= 24 ? 4 : this.maxNumberOfWorkers >= 16 ? 2 : 1;
    return Array.from({ length: this.maxNumberOfWorkers }, (value, index) => {
      const currentNumberOfWorkers = index + 1;
      return currentNumberOfWorkers % modBy === 0 ? currentNumberOfWorkers.toString() : '';
    });
  }

  get scoreInputs() {
    return Object.keys(this.options.geneScores || {})
      .map((key) => ({ key: key as GeneEnum, value: this.options.geneScores?.[key as GeneEnum] }))
      .filter((item, index) => index < 5);
  }

  get minimumTrackedScoreDerived() {
    const highestGeneScore = Math.max(...Object.values(this.options.geneScores));
    return highestGeneScore * 4;
  }

  created() {
    this.shouldDisplayScreenCaptureOptions = isScanningAvailable();

    // In case someone would change a processor in his PC and his navigator.hardwareConcurrency would be lower than before,
    // for best performance we want to maintain a value that is not higher than navigator.hardwareConcurrency.
    const reasonableNumberOfWorkers = Math.min(this.currentlySetOptions.numberOfWorkers, this.maxNumberOfWorkers);
    this.currentlySetOptions.numberOfWorkers = reasonableNumberOfWorkers;
    this.options.numberOfWorkers = reasonableNumberOfWorkers;
  }

  mounted() {
    this.$vuetify.theme.dark = this.currentlySetOptions.darkMode;
  }

  handleModifyMinimumTrackedScoreManuallyChange(value: boolean) {
    if (!value) {
      this.options.minimumTrackedScore = this.minimumTrackedScoreDerived;
    }
  }

  handleGeneScoreChange() {
    if (!this.options.modifyMinimumTrackedScoreManually) {
      this.options.minimumTrackedScore = this.minimumTrackedScoreDerived;
    }
  }

  undoChanges() {
    this.resetTo(this.currentlySetOptions);
  }

  resetToDefaults() {
    this.resetTo(DEFAULT_OPTIONS);
  }

  resetTo(options: ApplicationOptions) {
    this.options = JSON.parse(JSON.stringify(options));
    this.crossbreedingSaplingsNumberRange = [
      this.options.minCrossbreedingSaplingsNumber,
      this.options.maxCrossbreedingSaplingsNumber
    ];
    this.$vuetify.theme.dark = this.options.darkMode;
  }

  getOptions() {
    return this.currentlySetOptions;
  }

  handleCloseButtonClick() {
    this.undoChanges();
    this.isDialogOpen = false;
  }

  setOptions() {
    this.options.minCrossbreedingSaplingsNumber = this.crossbreedingSaplingsNumberRange[0];
    this.options.maxCrossbreedingSaplingsNumber = this.crossbreedingSaplingsNumberRange[1];
    this.currentlySetOptions = JSON.parse(JSON.stringify(this.options));
    this.$emit('options-set', this.currentlySetOptions);
    this.isDialogOpen = false;
  }

  saveOptions() {
    this.setOptions();
    this.persistOptionsToLocalStorage();
    this.isDialogOpen = false;
  }

  handleUIModeChange(checked: boolean) {
    this.$vuetify.theme.dark = checked;
  }

  stepSaplingsAdded(delta: number) {
    const v = Number(this.options.numberOfSaplingsAddedBetweenGenerations) || 1;
    this.options.numberOfSaplingsAddedBetweenGenerations = Math.max(1, v + delta);
  }

  stepGeneScore(key: string, delta: number) {
    const v = Number(this.options.geneScores[key as GeneEnum]) || 0;
    this.options.geneScores[key as GeneEnum] = Math.max(-1, Math.min(1, Math.round((v + delta) * 100) / 100));
    this.handleGeneScoreChange();
  }

  stepMinScore(delta: number) {
    if (!this.options.modifyMinimumTrackedScoreManually) return;
    const v = Number(this.options.minimumTrackedScore) || 0;
    this.options.minimumTrackedScore = Math.max(-6, Math.min(6, v + delta));
  }

  resetScannerCalibration() {
    this.options.scannerOffsetX = 0;
    this.options.scannerOffsetY = 0;
    this.options.scannerGeneScale = 1;
  }

  persistOptionsToLocalStorage() {
    try {
      localStorage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify(this.currentlySetOptions));
    } catch {
      /* private mode / quota */
    }
  }
}
